import {Injectable} from '@angular/core';
import {Papa, ParseResult} from 'ngx-papaparse';
import {TranslateService} from '@ngx-translate/core';
import {StatusEventDetails} from '@models/machine/status-event-signal/status-event-details';
import {StatusEventSignalParsingException} from '@models/machine/status-event-signal/status-event-signal-parsing-exception';
import {
  StatusEventExceptionTypes
} from '@constants/machine/status-event-signal/status-event-exception-types.enum';
import {
  BIT_PARSING_MAX_ALLOWED_ERROR_CODES,
  COLUMNS_SEPARATOR,
  STATUS_EVENT_SIGNAL_LANGUAGE_COLUMN_PREFIX,
  VALUE_PARSING_MAX_ALLOWED_ERROR_CODES
} from '@constants/machine/status-event-signal/status-event-signal.constants';
import {StatusEventType} from '@constants/machine/status-event-signal/status-event-type.enum';
import {StatusEventSignalColumnKind} from '@constants/machine/status-event-signal/status-event-signal-column-type.enum';
import {
  StatusEventSignalValueParsingType
} from '@constants/machine/status-event-signal/status-event-signal-value-parsing-type.enum';

@Injectable({
  providedIn: 'root'
})
export class StatusEventSignalCsvImportService {

  private NEW_LINE: string = '</br>';
  private CSV_PARSER_OPTIONS = {
    delimiter: COLUMNS_SEPARATOR,
    quoteChar: '"',
    header: true,
    skipEmptyLines: true,

  };

  constructor(private csvParser: Papa,
              private translate: TranslateService) {
  }

  public parseCsvToStatusEventDetails(parsingType: StatusEventSignalValueParsingType, inputString: string): Map<string, StatusEventDetails> {
    console.log('Started CSV paring for parsing type:' + parsingType);
    this.handleCsvInvalidInput(inputString);

    const parseResult: ParseResult = this.parseCsv(inputString);
    this.handleCsvInvalidParsedOutput(parseResult);

    this.handleCsvParsingErrors(parseResult);

    this.handleCsvLargeNumberOfCodes(parsingType, parseResult);

    const result: Map<string, StatusEventDetails> = new Map<string, StatusEventDetails>();

    const duplicatedCodes = new Set<string>();
    const mandatoryFieldsMissing = new Set<string>();
    const unsupportedTypeCodes = new Set<string>();
    const bitParsingInvalidCodes = new Set<string>();

    console.log('Parsing data rows.');
    for (let rowIndex = 0; rowIndex < parseResult.data.length; rowIndex++) {
      const dataRow = parseResult.data[rowIndex];
      const statusEventDetails = this.parseCsvDataRow(dataRow);
      const eventCode = statusEventDetails.code;

      if (!this.verifyRowContainsMandatoryFields(statusEventDetails)) {
        mandatoryFieldsMissing.add(this.generateDetailsString(rowIndex, statusEventDetails, StatusEventExceptionTypes.MANDATORY_VALUE_MISSING));
      }

      if (statusEventDetails.type === StatusEventType.UNSUPPORTED) {
        unsupportedTypeCodes.add(this.generateDetailsString(rowIndex, statusEventDetails, StatusEventExceptionTypes.UNSUPPORTED_EVENT_TYPES));
      }

      if (parsingType === StatusEventSignalValueParsingType.BIT && !this.stringIsPositiveInteger(eventCode)) {
        bitParsingInvalidCodes.add(this.generateDetailsString(rowIndex, statusEventDetails, StatusEventExceptionTypes.UNSUPPORTED_EVENT_CODES));
      }

      if (result.has(eventCode)) {
        duplicatedCodes.add(this.generateDetailsString(rowIndex, statusEventDetails, StatusEventExceptionTypes.DUPLICATED_CODES));
      } else {
        result.set(eventCode, statusEventDetails);
      }
    }
    console.log('Parsed data');

    this.handleMissingMandatoryFields(mandatoryFieldsMissing);
    this.handleUnsupportedEventTypes(unsupportedTypeCodes);
    this.handleDuplicatedEventCodes(duplicatedCodes);
    this.handleUnsupportedEventCodesForParsingType(bitParsingInvalidCodes, parsingType);

    return result;
  }

  private generateDetailsString(rowIndex: number, statusEventDetails: StatusEventDetails, issueType: StatusEventExceptionTypes) {
    let details: string = 'Row: ' + rowIndex + '. ';

    details += this.generateInvalidCodeDetails(statusEventDetails, issueType);
    details += this.generateEventTypeDetails(statusEventDetails, issueType);
    details += this.generateEventDescriptionDetails(statusEventDetails, issueType);
    details += this.NEW_LINE;

    return details;

  }

  private generateInvalidCodeDetails(field: StatusEventDetails, issueType: StatusEventExceptionTypes) {

    if (!field.code) {
      return '{CODE_MISSING}'.toBold() + ', ';
    }

    if (issueType == StatusEventExceptionTypes.DUPLICATED_CODES) {
      return (field.code + ' {DUPLICATED}').toBold() + ' ,';
    }

    if (issueType == StatusEventExceptionTypes.UNSUPPORTED_EVENT_CODES) {
      return (field.code + ' {UNSUPPORTED}').toBold() + ', ';
    }

    return field.code + ', ';
  }

  private generateEventTypeDetails(field: StatusEventDetails, issueType: StatusEventExceptionTypes) {

    if (!field.type) {
      return '{TYPE_MISSING}'.toBold() + ', ';
    }

    if (issueType == StatusEventExceptionTypes.UNSUPPORTED_EVENT_TYPES) {
      return (field.initialType + ' {UNSUPPORTED}').toBold() + ', ';
    }

    return field.type + ', ';

  }

  private generateEventDescriptionDetails(fieldValue: StatusEventDetails, _issueType: StatusEventExceptionTypes) {
    if (!fieldValue.description) {
      return '{DESCRIPTION_MISSING}, '.toBold();
    }
    return fieldValue.description + ', ';

  }

  private handleCsvInvalidInput(inputString: string): void {
    console.log('Validating CSV input');
    let message: string = '';
    if (inputString == undefined || inputString.length === 0) {
      message = this.translate.instant('The provided CSV content is not valid [empty]');
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.EMPTY_FILE, message);
    }
  }

  private handleCsvInvalidParsedOutput(parseResult: ParseResult): void {
    console.log('Validating for empty output');
    if (!parseResult || !parseResult.data || parseResult.data.length === 0) {
      const message = this.translate.instant('There was en error in parsing the CSV, the parsed output is empty.');
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.EMPTY_PARSED_DATA, message);
    }

  }

  private handleCsvParsingErrors(parseResult: ParseResult): void {
    console.log('Validating for output errors during parsing');
    const errors = parseResult.errors;
    if (errors.length <= 0) {
      return;
    }
    const errorMessage = this.translate.instant('Row %row. %type - %message') + this.NEW_LINE;
    let errorMessages: string = '';
    for (const error of errors) {
      errorMessages += errorMessage.replace('%row', (error.row + 2) + '')
      .replace('%type', error.type)
      .replace('%message', error.message);

    }
    throw new StatusEventSignalParsingException(StatusEventExceptionTypes.ERROR_PARSING, errorMessages);
  }

  private handleCsvLargeNumberOfCodes(parsingType: StatusEventSignalValueParsingType, parseResult: ParseResult): void {
    console.log('Validating output event codes limits');
    let maxNumberOfCodes: number = VALUE_PARSING_MAX_ALLOWED_ERROR_CODES;
    if (parsingType == StatusEventSignalValueParsingType.BIT) {
      maxNumberOfCodes = BIT_PARSING_MAX_ALLOWED_ERROR_CODES;
    }

    if (parseResult.data.length > maxNumberOfCodes) {
      let message = this.translate.instant('The defined number of event codes (%1) exceeds the max allowed number of %2 for parsing type %3');
      message = message.replace('%1', parseResult.data.length);
      message = message.replace('%2', maxNumberOfCodes + '');
      message = message.replace('%3', parsingType + '');
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.TOO_MANY_CODES, message);
    }
  }

  private handleMissingMandatoryFields(mandatoryFieldsMissing: Set<string>): void {
    if (mandatoryFieldsMissing.size > 0) {
      let message = this.translate.instant('Import Failure. There are items without mandatory values');
      message += this.NEW_LINE + Array.from(mandatoryFieldsMissing).join('');
      console.log(message);
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.MANDATORY_VALUE_MISSING, message);
    }

  }

  private parseCsvDataRow(row: any): StatusEventDetails {
    let code: string = '';
    let type: StatusEventType = StatusEventType.UNDEFINED;
    let description: string = '';
    const metadata: Map<string, string> = new Map<string, string>();
    const localization: Map<string, string> = new Map<string, string>();

    let initialEventType: string = '';
    for (const key in row) {
      const keyValue = this.cleanupString(row[key]);
      const keyType = this.determineColumnType(key);


      switch (keyType) {
        case StatusEventSignalColumnKind.CODE:
          code = this.cleanupString(keyValue);
          break;
        case StatusEventSignalColumnKind.TYPE:
          type = this.determineEventType(keyValue);
          initialEventType = keyValue;
          break;
        case StatusEventSignalColumnKind.DESCRIPTION:
          description = this.cleanupString(keyValue);
          break;
        case StatusEventSignalColumnKind.LANGUAGE:
          const languageCode = key.replace(STATUS_EVENT_SIGNAL_LANGUAGE_COLUMN_PREFIX, '');
          localization.set(languageCode, keyValue);
          break;
        case StatusEventSignalColumnKind.OTHER:
          metadata.set(key, keyValue);
          break;
      }
    }
    const details = new StatusEventDetails(code, type, description, metadata, localization);
    details.initialType = initialEventType;
    return details;
  }

  private determineEventType(eventTypeString: string): StatusEventType {
    if (eventTypeString === null || eventTypeString.length === 0) {
      console.log('UNDEFINED. eventTypeString: ' + eventTypeString);
      return StatusEventType.UNDEFINED;
    }
    const listOfEventTypesValues = (<any>Object).values(StatusEventType);
    const listOfEventTypesKeys = (<any>Object).keys(StatusEventType);
    const index = listOfEventTypesValues.indexOf(eventTypeString);
    if (index >= 0) {
      return StatusEventType[listOfEventTypesKeys[index]];
    } else {
      console.log('UNSUPPORTED. eventTypeString: ' + eventTypeString);
      return StatusEventType.UNSUPPORTED;
    }

  }

  private cleanupString(input: string): string {
    return input.trim();
  }

  private determineColumnType(key: string): StatusEventSignalColumnKind {
    if (key.toLowerCase() === StatusEventSignalColumnKind.CODE.toLowerCase()) {
      return StatusEventSignalColumnKind.CODE;
    }
    if (key.toLowerCase() === StatusEventSignalColumnKind.TYPE.toLowerCase()) {
      return StatusEventSignalColumnKind.TYPE;
    }

    if (key.toLowerCase() === StatusEventSignalColumnKind.DESCRIPTION.toLowerCase()) {
      return StatusEventSignalColumnKind.DESCRIPTION;
    }

    if (key.indexOf(STATUS_EVENT_SIGNAL_LANGUAGE_COLUMN_PREFIX) >= 0) {
      return StatusEventSignalColumnKind.LANGUAGE;
    }
    return StatusEventSignalColumnKind.OTHER;
  }

  private parseCsv(inputString: string): ParseResult {
    return this.csvParser.parse(inputString, this.CSV_PARSER_OPTIONS);
  }


  private verifyRowContainsMandatoryFields(statusEventDetails: StatusEventDetails): boolean {

    if ((statusEventDetails.code === null || statusEventDetails.code.length === 0) ||
        (statusEventDetails.type === null || statusEventDetails.type.length === 0) ||
        (statusEventDetails.description === null || statusEventDetails.description.length === 0)) {
      console.log('Code: ' + statusEventDetails.code + ', type: ' + statusEventDetails.type + ', description: ' + statusEventDetails.description);
      return false;
    }
    return true;
  }

  private handleUnsupportedEventTypes(unsupportedTypeCodes: Set<string>) {
    if (unsupportedTypeCodes.size > 0) {
      let message = this.translate.instant('Import Failure! There are events codes with unsupported event types.');
      message += this.NEW_LINE + Array.from(unsupportedTypeCodes).join('');
      console.log(message);
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.UNSUPPORTED_EVENT_TYPES, message);
    }
  }

  private handleDuplicatedEventCodes(duplicatedCodes: Set<string>) {
    if (duplicatedCodes.size > 0) {
      let message = this.translate.instant('There are duplicated event codes defined. Import failure');
      message += this.NEW_LINE + Array.from(duplicatedCodes).join('');
      console.log(message);
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.DUPLICATED_CODES, message);
    }
  }

  private stringIsPositiveInteger(value: string): boolean {
    if (!value) {
      return false;
    }
    const number = Number(value);
    return Number.isInteger(number) && number >= 0;

  }

  private handleUnsupportedEventCodesForParsingType(bitParsingInvalidCodes: Set<string>, parsingType: StatusEventSignalValueParsingType) {
    if (parsingType === StatusEventSignalValueParsingType.BIT && bitParsingInvalidCodes.size > 0) {
      let message = this.translate.instant('There are unsupported event codes defined. Import failure');
      message += this.NEW_LINE + Array.from(bitParsingInvalidCodes).join('');
      console.log(message);
      throw new StatusEventSignalParsingException(StatusEventExceptionTypes.UNSUPPORTED_EVENT_CODES, message);
    }
  }

}
